/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { Card, Select, FieldGroup } from "core/components";
import { AllocationRuleFormData, ProjectPropertiesFormData, ProjectStatus, UserFormData, WarehouseFormData } from "core/schema";
import { edit_admin_template_path } from "routes";
import DocumentFields from "./DocumentFields";

type ForemanOption = {
  id: number;
  first_name: string;
  last_name: string;
  active: boolean;
};

type ProjectPropertiesFormProps = {
  project: ProjectPropertiesFormData;
  users: Array<UserFormData>;
  foremen: Array<ForemanOption>;
  warehouses: Array<WarehouseFormData>;
  allocation_rules: Array<AllocationRuleFormData>;
  can_edit: boolean;
};

const ProjectPropertiesForm = ({ project, template_id, users, foremen = [], warehouses, allocation_rules, can_edit }: ProjectPropertiesFormProps): JSX.Element => {
  const {
    control,
    watch,
    formState: { errors }
  } = useFormContext();

  const status = watch("status");
  const foremanOptions = Array.isArray(foremen) ? foremen : [];
  const isCompletedOrCancelled = () => [ProjectStatus.Completed, ProjectStatus.Cancelled].includes(project.status);
  const isDisabled = () => !can_edit || isCompletedOrCancelled();

  return (
    <Card.Body>
      <Container fluid={true}>
        <Row>
          <Col>
            <h5>Project Properties</h5>
            <Row className="mt-4">
              <Col>
                <Controller
                  name="manager_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Project Manager"
                      placeholder="Select Project Manager"
                      required
                      options={users}
                      noOptionsMessage={() => "No project managers available."}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={users.find((user) => user.id == field.value) || null}
                      errorMessage={errors.manager_id?.message}
                      disabled={isDisabled()}
                    />
                  )}
                />
              </Col>
              <Col>
                <Controller
                  name="architect_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Architect In-Charge"
                      placeholder="Select Architech In-Charge"
                      required
                      options={users}
                      noOptionsMessage={() => "No architects available."}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={users.find((user) => user.id == field.value) || null}
                      errorMessage={errors.architect_id?.message}
                      disabled={isDisabled()}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name="engineer_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Engineer In-Charge"
                      placeholder="Select Engineer In-Charge"
                      required={status != ProjectStatus.Pending}
                      options={users}
                      noOptionsMessage={() => "No engineers available."}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={users.find((user) => user.id == field.value) || null}
                      errorMessage={errors.engineer_id?.message}
                      disabled={isCompletedOrCancelled()}
                    />
                  )}
                />
              </Col>
              <Col>
                <Controller
                  name="foreman_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Foreman In-Charge"
                      placeholder="Select Foreman In-Charge"
                      required={status != ProjectStatus.Pending}
                      options={foremanOptions}
                      noOptionsMessage={() => "No foremen available."}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={foremanOptions.find((f) => f.id == field.value) || null}
                      errorMessage={errors.foreman_id?.message}
                      disabled={isCompletedOrCancelled()}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name="warehouse_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Designated Warehouse"
                      placeholder="Select a Warehouse"
                      required
                      options={warehouses}
                      noOptionsMessage={() => "No warehouses available."}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={warehouses.find((warehouse) => warehouse.id == field.value) || null}
                      errorMessage={errors.warehouse_id?.message}
                      disabled={isDisabled()}
                    />
                  )}
                />
              </Col>
            </Row>
            <h5>Allocation Settings</h5>
            <Row className="mt-2">
              <Col>
                <Controller
                  name="allocation_rule_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Allocation Rule"
                      placeholder="Select Allocation Rule"
                      required
                      options={allocation_rules}
                      noOptionsMessage={() => "No allocation rules available."}
                      getOptionLabel={(option) => `${option.code} ${option.name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={allocation_rules.find((allocation_rule) => allocation_rule.id == field.value) || null}
                      errorMessage={errors.allocation_rule_id?.message}
                      disabled={isDisabled()}
                    />
                  )}
                />
              </Col>
            </Row>
            <h5>Authorized By</h5>
            <Row className="mt-2">
              <Col>
                <Controller
                  name="authorized_by_design_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Design Department"
                      placeholder="Select from Design"
                      required
                      options={users}
                      noOptionsMessage={() => "No users available."}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={users.find((user) => user.id == field.value) || null}
                      errorMessage={errors.authorized_by_design_id?.message}
                      disabled={isDisabled()}
                    />
                  )}
                />
              </Col>
              <Col>
                <Controller
                  name="authorized_by_sales_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Sales Department"
                      placeholder="Select from Sales"
                      required
                      options={users}
                      noOptionsMessage={() => "No users available."}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={users.find((user) => user.id == field.value) || null}
                      errorMessage={errors.authorized_by_sales_id?.message}
                      disabled={isDisabled()}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name="authorized_by_finance_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Management"
                      placeholder="Select from Management"
                      required
                      options={users}
                      noOptionsMessage={() => "No users available."}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      getOptionValue={(option) => option.id}
                      isOptionDisabled={(option) => !option.active}
                      onChange={(newOption) => {
                        field.onChange(newOption?.id);
                      }}
                      value={users.find((user) => user.id == field.value) || null}
                      errorMessage={errors.authorized_by_finance_id?.message}
                      disabled={isDisabled()}
                    />
                  )}
                />
              </Col>
            </Row>
          </Col>
          <Col>
            <h5 className="h5 mb-2">Supporting Documents</h5>
            <DocumentFields readOnly={isDisabled()} />
            {(project.template_id || template_id) && (
              <FieldGroup label="Bill of Materials">
                <Row>
                  <a
                    className="text-bold"
                    href={edit_admin_template_path(project.template_id || template_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Template
                  </a>
                </Row>
              </FieldGroup>
            )}
            <Row>
              <Col>
                <h5>Architect's Notes</h5>
                <Controller
                  name="remarks"
                  control={control}
                  render={({ field }) => (
                    <FieldGroup errorMessage={errors.remarks?.message}>
                      <textarea {...field} className="field mt-2" placeholder="Remarks" maxLength={255} value={field.value} readOnly={isDisabled()} />
                    </FieldGroup>
                  )}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Card.Body>
  );
};

export default ProjectPropertiesForm;
